import { useCallback, useEffect, useState, type RefObject } from "react";
import {
  coerceEmbedWidthPercent,
  getEmbedContentWidthPx,
  isEmbedNarrowWidth,
} from "./embedWidth";
import {
  coerceCreatorPageWidthPercent,
  getCreatorPageContentWidthPx,
  getNarrowShellContentWidthPx,
  isCreatorPageNarrowWidth,
} from "../features/creator-pages/creatorPageWidth";

/** Matches --feed-card-min-width / --search-feed-card-min-width (Search + Individual). */
export const GRID_TILE_WIDTH = 140;
/** Matches --feed-post-gap / --search-feed-post-gap (16px row + column). */
export const GRID_TILE_GAP = 16;

export type GridLayoutOptions = {
  isCreatorPage?: boolean;
  isEmbed?: boolean;
  screenWidth?: number;
};

export type LayoutContentWidthOptions = {
  maxWidthPercent: number;
  narrowRemLayout?: boolean;
  isCreatorPage?: boolean;
  isEmbed?: boolean;
};

function getViewportWidth(screenWidth?: number): number {
  if (typeof screenWidth === "number") return screenWidth;
  if (typeof window !== "undefined") return window.innerWidth;
  return 1200;
}

export function desktopColsFromContentWidth(
  contentWidth: number,
  maxCols = 8
): number {
  if (contentWidth <= 0) return 1;
  const exact =
    (contentWidth + GRID_TILE_GAP) / (GRID_TILE_WIDTH + GRID_TILE_GAP);
  return Math.max(1, Math.min(maxCols, Math.floor(exact)));
}

/**
 * CSS grid that fills the container based on tile width — not viewport breakpoints.
 * Prefer CSS `var(--feed-card-min-width)` when possible; this JS helper stays for
 * inline/legacy callers and mirrors the shared feed min-width token.
 */
export function getResponsiveGridTemplateColumns(
  minTileWidth = GRID_TILE_WIDTH
): string {
  return `repeat(auto-fill, minmax(min(100%, ${minTileWidth}px), 1fr))`;
}

/**
 * Effective inner width for post grids from shell constraints (rem cap, % cap, padding).
 * Uses viewport width only to resolve mobile vs desktop padding — not for column breakpoints.
 */
export function getLayoutContentWidthPx(
  screenWidth: number,
  options: LayoutContentWidthOptions
): number {
  const { maxWidthPercent, isCreatorPage, isEmbed } = options;

  if (isCreatorPage) {
    return getCreatorPageContentWidthPx(screenWidth, maxWidthPercent);
  }

  if (isEmbed) {
    return getEmbedContentWidthPx(screenWidth, maxWidthPercent);
  }

  return getNarrowShellContentWidthPx(screenWidth);
}

/**
 * Desktop column targets from width %.
 * Embed + creator pages: rem-based shell (60% = 45rem) + content fit.
 * Standalone / no-page shell (~45rem) → 4 desktop cols (responsive will reduce).
 */
export function maxWidthPercentToDesktopGridColumns(
  pct: number,
  narrowRemLayout?: boolean,
  layoutOptions?: GridLayoutOptions
): number {
  if (narrowRemLayout) return 4;

  if (layoutOptions?.isEmbed) {
    const screenWidth = getViewportWidth(layoutOptions.screenWidth);
    if (isEmbedNarrowWidth(pct)) {
      return 4;
    }
    const contentWidth = getEmbedContentWidthPx(screenWidth, pct);
    return desktopColsFromContentWidth(contentWidth);
  }

  if (layoutOptions?.isCreatorPage) {
    const screenWidth = getViewportWidth(layoutOptions.screenWidth);
    if (isCreatorPageNarrowWidth(pct)) {
      return 4;
    }
    const contentWidth = getCreatorPageContentWidthPx(screenWidth, pct);
    return desktopColsFromContentWidth(contentWidth);
  }

  const contentWidth = getNarrowShellContentWidthPx(
    getViewportWidth(layoutOptions?.screenWidth)
  );
  return desktopColsFromContentWidth(contentWidth);
}

/** Match `RenderIndividualUI` grid: scale down on smaller viewports. */
export function getResponsiveGridColumnCountFullwidth(
  desktopCols: number,
  innerWidth: number
): number {
  const w = innerWidth;
  if (w >= 1400) return desktopCols;
  if (w >= 1200) return Math.min(7, desktopCols);
  if (w >= 991) return Math.min(6, desktopCols);
  if (w >= 768) return Math.min(5, desktopCols);
  if (w >= 580) return Math.min(4, desktopCols);
  if (w >= 420) return Math.min(3, desktopCols);
  return Math.min(2, desktopCols);
}

export function getResponsiveGridColumnCountHalfwidth(
  desktopCols: number,
  innerWidth: number
): number {
  const w = innerWidth;
  if (w >= 1500) return desktopCols;
  if (w >= 1200) return Math.min(6, desktopCols);
  if (w >= 950) return Math.min(5, desktopCols);
  if (w >= 750) return Math.min(4, desktopCols);
  if (w >= 550) return Math.min(3, desktopCols);
  return Math.min(2, desktopCols);
}

export function getResponsiveGridColumnCountSmallwidth(
  desktopCols: number,
  innerWidth: number
): number {
  const w = innerWidth;
  if (w >= 1400) return 6;
  if (w >= 1200) return Math.min(6, desktopCols);
  if (w >= 768) return Math.min(4, desktopCols);
  if (w >= 576) return Math.min(3, desktopCols);
  return Math.min(2, desktopCols);
}

export type WidthControlBand = "full" | "half" | "small" | "standalone";

export function maxWidthPercentToWidthBand(
  pct: number,
  narrowRemLayout?: boolean,
  isCreatorPage?: boolean,
  isEmbed?: boolean
): WidthControlBand {
  if (narrowRemLayout) return "standalone";
  if (isCreatorPage && isCreatorPageNarrowWidth(pct)) return "standalone";
  if (isEmbed && isEmbedNarrowWidth(pct)) return "standalone";

  if (isCreatorPage) {
    const n = coerceCreatorPageWidthPercent(pct);
    if (n >= 88) return "full";
    if (n >= 73) return "half";
    return "small";
  }

  if (isEmbed) {
    const n = coerceEmbedWidthPercent(pct);
    if (n >= 88) return "full";
    if (n >= 73) return "half";
    return "small";
  }

  return "standalone";
}

export function getResponsiveGridColumnCountForBand(
  desktopCols: number,
  innerWidth: number,
  band: WidthControlBand
): number {
  if (band === "full") return getResponsiveGridColumnCountFullwidth(desktopCols, innerWidth);
  if (band === "half") return getResponsiveGridColumnCountHalfwidth(desktopCols, innerWidth);
  if (band === "small") return getResponsiveGridColumnCountSmallwidth(desktopCols, innerWidth);
  const w = innerWidth;
  if (w < 450) return Math.min(2, desktopCols);
  if (w < 575) return Math.min(3, desktopCols);
  return Math.min(4, desktopCols);
}

export type ResponsiveGridOptions = {
  maxWidthPercent: number;
  narrowRemLayout?: boolean;
  isCreatorPage?: boolean;
  isEmbed?: boolean;
};

function resolveResponsiveGridColumnCount(
  desktopCols: number,
  options?: ResponsiveGridOptions,
  measuredContentWidth?: number | null
): number {
  if (!options) {
    if (typeof window === "undefined") return desktopCols;
    return desktopColsFromContentWidth(
      getNarrowShellContentWidthPx(window.innerWidth)
    );
  }

  const layoutOptions: LayoutContentWidthOptions = {
    maxWidthPercent: options.maxWidthPercent,
    narrowRemLayout: options.narrowRemLayout,
    isCreatorPage: options.isCreatorPage,
    isEmbed: options.isEmbed,
  };

  const contentWidth =
    measuredContentWidth && measuredContentWidth > 0
      ? measuredContentWidth
      : typeof window !== "undefined"
        ? getLayoutContentWidthPx(window.innerWidth, layoutOptions)
        : getLayoutContentWidthPx(1200, layoutOptions);

  return desktopColsFromContentWidth(contentWidth);
}

export function useResponsiveGridColumnCount(
  desktopCols: number,
  options?: ResponsiveGridOptions
): number {
  const maxWidthPercent = options?.maxWidthPercent;
  const narrowRemLayout = options?.narrowRemLayout;
  const isCreatorPage = options?.isCreatorPage;
  const isEmbed = options?.isEmbed;
  const hasOptions = options != null;

  const compute = useCallback(
    (d: number) =>
      resolveResponsiveGridColumnCount(
        d,
        hasOptions
          ? {
              maxWidthPercent: maxWidthPercent ?? 100,
              narrowRemLayout,
              isCreatorPage,
              isEmbed,
            }
          : undefined
      ),
    [hasOptions, maxWidthPercent, narrowRemLayout, isCreatorPage, isEmbed]
  );

  const [n, setN] = useState(() => compute(desktopCols));

  useEffect(() => {
    setN(compute(desktopCols));
    let debounceId: ReturnType<typeof setTimeout> | undefined;

    const onResize = () => {
      if (debounceId) clearTimeout(debounceId);
      // Recompute only after resize settles so the card shell is not repositioned every frame.
      debounceId = setTimeout(() => {
        const next = compute(desktopCols);
        setN((prev) => (prev === next ? prev : next));
      }, 120);
    };

    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      if (debounceId) clearTimeout(debounceId);
    };
  }, [desktopCols, compute]);

  return n;
}

/** Measure the live grid container and derive column count from actual width. */
export function useMeasuredGridColumnCount(
  containerRef: RefObject<HTMLElement | null>,
  options?: ResponsiveGridOptions
): number {
  const maxWidthPercent = options?.maxWidthPercent;
  const narrowRemLayout = options?.narrowRemLayout;
  const isCreatorPage = options?.isCreatorPage;
  const isEmbed = options?.isEmbed;
  const hasOptions = options != null;

  const resolveCols = useCallback(
    (measuredContentWidth?: number | null) =>
      resolveResponsiveGridColumnCount(
        4,
        hasOptions
          ? {
              maxWidthPercent: maxWidthPercent ?? 100,
              narrowRemLayout,
              isCreatorPage,
              isEmbed,
            }
          : undefined,
        measuredContentWidth
      ),
    [hasOptions, maxWidthPercent, narrowRemLayout, isCreatorPage, isEmbed]
  );

  const [columnCount, setColumnCount] = useState(() => resolveCols(null));

  useEffect(() => {
    let observer: ResizeObserver | null = null;
    let rafId = 0;
    let debounceId: ReturnType<typeof setTimeout> | undefined;

    const applyWidth = (width: number) => {
      if (width <= 0) return;
      const next = resolveCols(width);
      setColumnCount((prev) => (prev === next ? prev : next));
    };

    const attach = () => {
      const element = containerRef?.current;
      if (!element || typeof ResizeObserver === "undefined") {
        if (!element) {
          rafId = window.requestAnimationFrame(attach);
        } else {
          setColumnCount(resolveCols(null));
        }
        return;
      }

      applyWidth(element.getBoundingClientRect().width);

      observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width;
        if (!(width && width > 0)) return;
        if (debounceId) clearTimeout(debounceId);
        // Only commit after resize settles, and only when column count changes.
        debounceId = setTimeout(() => applyWidth(width), 120);
      });

      observer.observe(element);
    };

    attach();

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      if (debounceId) clearTimeout(debounceId);
      observer?.disconnect();
    };
  }, [containerRef, resolveCols]);

  return columnCount;
}
